import React, { createContext, useState, useContext, useRef } from 'react';

interface DashboardContextProps {
    selectedStoreIds: string[];
    setSelectedStoreIds: (ids: string[]) => void;
    selectedPackageIds: string[];
    setSelectedPackageIds: (ids: string[]) => void;
    selectedStatus: string[];
    setSelectedStatus: (status: string[]) => void;
    differenceReported: boolean;
    setDifferenceReported: (value: boolean) => void;
    selectedSupplierIds: string[];
    setSelectedSupplierIds: (ids: string[]) => void;
    selectedParcPalettIds: string[];
    setSelectedParcPalettIds: (ids: string[]) => void;
    selectedWarehouseIds: string[];
    setSelectedWarehouseIds: (ids: string[]) => void;
    selectedStartDate: string | null;
    setSelectedStartDate: (date: string | null) => void;
    selectedEndDate: string | null;
    setSelectedEndDate: (date: string | null) => void;
    selectedVoucherStatus: string[];
    setSelectedVoucherStatus: (status: string[]) => void;
    selectedLitigeStatus: string[];
    setSelectedLitigeStatus: (status: string[]) => void;
    selectedBonDeTransport: string[];
    setSelectedBonDeTransport: (vouchers: string[]) => void;
    selectedInvoiceName: string;
    setSelectedInvoiceName: (name: string) => void;
    hasTableData: boolean;
    setHasTableData: (hasData: boolean) => void;
    storeFilterManuallySet: boolean;
    setStoreFilterManuallySet: (value: boolean) => void;
    refreshPackageCount: React.MutableRefObject<(() => Promise<void>) | null>;
}

const DashboardContext = createContext<DashboardContextProps | undefined>(undefined);

export const DashboardProvider: React.FC = ({ children }) => {
    const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
    const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
    const [differenceReported, setDifferenceReported] = useState<boolean>(false);
    const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
    const [selectedParcPalettIds, setSelectedParcPalettIds] = useState<string[]>([]);
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>([]);
    const [selectedStartDate, setSelectedStartDate] = useState<string | null>(null);
    const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);
    const [selectedVoucherStatus, setSelectedVoucherStatus] = useState<string[]>([]);
    const [selectedLitigeStatus, setSelectedLitigeStatus] = useState<string[]>([]);
    const [selectedBonDeTransport, setSelectedBonDeTransport] = useState<string[]>([]);
    const [selectedInvoiceName, setSelectedInvoiceName] = useState<string>('');
    const [hasTableData, setHasTableData] = useState(false);
    const [storeFilterManuallySet, setStoreFilterManuallySet] = useState(false);
    const refreshPackageCount = useRef<(() => Promise<void>) | null>(null);
    return (
        <DashboardContext.Provider
            value={{
                selectedStoreIds,
                setSelectedStoreIds,
                selectedPackageIds,
                setSelectedPackageIds,
                selectedStatus,
                setSelectedStatus,
                differenceReported,
                setDifferenceReported,
                selectedSupplierIds,
                setSelectedSupplierIds,
                selectedParcPalettIds,
                setSelectedParcPalettIds,
                selectedWarehouseIds,
                setSelectedWarehouseIds,
                selectedStartDate,
                setSelectedStartDate,
                selectedEndDate,
                setSelectedEndDate,
                selectedVoucherStatus,
                setSelectedVoucherStatus,
                selectedLitigeStatus,
                setSelectedLitigeStatus,
                selectedBonDeTransport,
                setSelectedBonDeTransport,
                selectedInvoiceName,
                setSelectedInvoiceName,
                hasTableData,
                setHasTableData,
                storeFilterManuallySet,
                setStoreFilterManuallySet,
                refreshPackageCount,
            }}
        >
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboardContext = (): DashboardContextProps => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboardContext must be used within a Dashboard');
    }
    return context;
};
